import React from "react";
import { CheckCircle2, X } from "lucide-react";
import { useOfflineWarmupStatus } from "../state/OfflineWarmupStatusContext";

const STORAGE_KEY = "phmis.offlineWarmupToast.dismissed";

export const WarmupToast: React.FC = () => {
  const status = useOfflineWarmupStatus();
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [visible, setVisible] = React.useState(false);
  const shownRef = React.useRef(false);

  React.useEffect(() => {
    if (dismissed) return;
    if (status.state === "success" && status.total > 0 && !shownRef.current) {
      shownRef.current = true;
      setVisible(true);
    }
  }, [dismissed, status.state, status.total]);

  const handleDismiss = React.useCallback(() => {
    setVisible(false);
    setDismissed(true);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex max-w-sm animate-in fade-in slide-in-from-bottom rounded-2xl border border-emerald-200 bg-white p-4 shadow-xl">
      <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
        <CheckCircle2 className="h-5 w-5" aria-hidden />
      </div>
      <div className="mr-4 flex-1">
        <p className="text-sm font-semibold text-emerald-900">Offline cache ready</p>
        <p className="text-xs text-slate-600">
          {status.total} dataset{status.total === 1 ? "" : "s"} cached. You can keep capturing data even if the network drops.
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss offline warmup notification"
        className="text-slate-400 transition hover:text-slate-600"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
};
